import React, { useState, useMemo, useEffect, useDeferredValue } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { RiFilter2Line } from "react-icons/ri";
import { usePatientAuthFlags } from "../../Hooks/usePatientAuthFlags";
import { matchesOrderedTokenSearch } from "../../utils/orderedTokenSearch";

// Utility function to format date to human-readable format
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  });
};

const TableSection = ({
  tableData = [],
  macrosData = [],
  handleEditClick,
  handleEditMacro,
  handleDeleteClick,
  loading,
  canManageDefaultTemplates = false,
  canCreateOrEditTemplates = true,
  displayHeaderItems,
  headerTableItems = {},
  displayFooterItems,
  footerTableItems = {},
  hideTemplateFields = false,
  selectedCard,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [filterType, setFilterType] = useState("template");
  const [macroFilterType, setMacroFilterType] = useState("macroName"); // Separate state for macros table filter type
  const [showMacroSearch, setShowMacroSearch] = useState(false); // State to control the visibility of macro search bar
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const { isWixAuth, isCreAuth, wixUser, creUser } = usePatientAuthFlags();
  const rowsPerPage = 8;
  const trimmedSearch = searchInput.trim();
  const deferredSearchQuery = useDeferredValue(trimmedSearch);

  useEffect(() => {
    if (selectedCard === 2 || selectedCard === 3) {
      setShowSearch(false);
    }
  }, [selectedCard]);
  
  const headerDataArray = useMemo(() => {
    return displayHeaderItems ? [headerTableItems] : [];
  }, [displayHeaderItems, headerTableItems]);
  
  const footerDataArray = useMemo(() => {
    return displayFooterItems ? [footerTableItems] : [];
  }, [displayFooterItems, footerTableItems]);
  
  const filteredData = useMemo(() => {
    if (deferredSearchQuery) {
      return tableData.filter((item) => {
        const value = item[filterType];
        return matchesOrderedTokenSearch(value, deferredSearchQuery);
      });
    }
    return tableData;
  }, [tableData, deferredSearchQuery, filterType]);
  const filteredMacrosData = useMemo(() => {
    if (deferredSearchQuery) {
      return macrosData.filter((macro) => {
        const value = macro[macroFilterType];
        return matchesOrderedTokenSearch(value, deferredSearchQuery);
      });
    }
    return macrosData;
  }, [macrosData, deferredSearchQuery, macroFilterType]);
  const dataToDisplay = useMemo(() => {
    if (displayHeaderItems) {
      return headerDataArray;
    } else if (displayFooterItems) {
      return footerDataArray;
    } else {
      return filteredData;
    }
  }, [
    displayHeaderItems,
    headerDataArray,
    displayFooterItems,
    footerDataArray,
    filteredData,
  ]);
  const totalItemsCount =
    selectedCard === 0 ? filteredMacrosData.length : dataToDisplay.length;
  const totalPages = Math.max(1, Math.ceil(totalItemsCount / rowsPerPage));

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCard, filterType, macroFilterType, trimmedSearch]);
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const currentData = Array.isArray(dataToDisplay)
    ? dataToDisplay.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
      )
    : [];
  const currentMacrosData = Array.isArray(filteredMacrosData)
    ? filteredMacrosData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
      )
    : [];
  const handleFilterTypeChange = (key) => {
    if (filterType === key) {
      setShowSearch(false);
      setFilterType("");
    } else {
      setFilterType(key);
      setShowSearch(true);
      setSearchInput("");
    }
  };

  const handleMacroFilterTypeChange = (key) => {
    if (macroFilterType === key) {
      setShowMacroSearch(false);
      setMacroFilterType("");
    } else {
      setMacroFilterType(key);
      setShowMacroSearch(true);
      setSearchInput("");
    }
  };
  const headers = [
    { label: "Template Names", key: "template" },
    { label: "Default", key: "isDefault" },
    { label: "Language", key: "language" },
    ...(isWixAuth || isCreAuth
      ? [{ label: "User Email", key: "userName" }]
      : [{ label: "User ID's", key: "userId" }]),
    { label: "Actions", key: "actions" },
  ];
  const macroHeaders = [
    ...(isWixAuth || isCreAuth ? [] : [{ label: "Macro ID", key: "macroId" }]),
    { label: "Macro Name", key: "macroName" },
    { label: "Edit", key: "Edit" },
  ];
  // Find the label of the currently selected filterType for the placeholder
  const currentFilterLabel =
    headers.find((header) => header.key === filterType)?.label || "Search";
  const currentMacroFilterLabel =
    macroHeaders.find((header) => header.key === macroFilterType)?.label ||
    "Search";
  const handleDeleteConfirm = () => {
    if (
      selectedCard !== 0 &&
      itemToDelete?.isDefault &&
      !canManageDefaultTemplates
    ) {
      setShowDeleteModal(false);
      setItemToDelete(null);
      return;
    }
    handleDeleteClick(itemToDelete);
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  return (
    <div className="mt-8 overflow-scroll bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-200">
      {showSearch && selectedCard !== 2 && selectedCard !== 3 && (
        <div className="mb-4">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={`Search by ${currentFilterLabel}`}
            className="px-4 py-2 border rounded w-full bg-gray-200 dark:bg-gray-700 dark:text-gray-200"
          />
        </div>
      )}
      {selectedCard !== 0 && (
        <table className="table-auto border bg-white dark:bg-gray-800 min-w-full text-left">
          <thead className="text-sm">
            <tr>
              {displayHeaderItems
                ? [
                    "Name",
                    "Date Created",
                    "Date Updated",
                    ...(isWixAuth || isCreAuth
                      ? []
                      : [
                          "Is Active",
                          "Organization ID",
                          "Section ID",
                          "Section Type ID",
                        ]),
                  ].map((header, index) => (
                    <th
                      key={index}
                      className="border px-2 py-2 min-w-[150px] cursor-pointer dark:text-gray-200"
                    >
                      <div className="flex gap-2 items-center">
                        <p>{header}</p>
                      </div>
                    </th>
                  ))
                : displayFooterItems
                ? [
                    "Name",
                    "Date Created",
                    "Date Updated",
                    ...(isWixAuth || isCreAuth
                      ? []
                      : [
                          "Is Active",
                          "Organization ID",
                          "Section ID",
                          "Section Type ID",
                        ]),
                  ].map((header, index) => (
                    <th
                      key={index}
                      className="border px-2 py-2 min-w-[150px] cursor-pointer dark:text-gray-200"
                    >
                      <div className="flex gap-2 items-center">
                        <p>{header}</p>
                      </div>
                    </th>
                  ))
                : headers.map((header) => (
                    <th
                      key={header.key}
                      className="border px-2 py-2 min-w-[150px] cursor-pointer dark:text-gray-200"
                      onClick={() => handleFilterTypeChange(header.key)}
                    >
                      <div className="flex gap-2 items-center">
                        <p>{header.label}</p>
                        {header.label !== "Edits" && (
                          <span
                            className="bg-gray-200 dark:bg-gray-700 w-6 h-6 rounded-full flex justify-center items-center"
                            onClick={() => setShowSearch((prev) => !prev)}
                          >
                            <RiFilter2Line className="text-gray-900 dark:text-gray-200" />
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
            </tr>
          </thead>
          <tbody className="text-xs">
            {loading
              ? [...Array(rowsPerPage)].map((_, index) => (
                  <tr key={index}>
                    {[
                      ...Array(
                        displayHeaderItems || displayFooterItems
                          ? 7
                          : !hideTemplateFields
                          ? 5
                          : 6
                      ),
                    ].map((_, i) => (
                      <td
                        key={i}
                        className="border border-gray-300 dark:border-gray-700 px-4 py-2"
                      >
                        <Skeleton height={20} />
                      </td>
                    ))}
                  </tr>
                ))
              : currentData.map((row, index) => (
                  <tr
                    key={index}
                    className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    {displayHeaderItems ? (
                      <>
                        <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                          Templates Header
                        </td>
                        <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                          {formatDate(row.dateCreated)}
                        </td>
                        <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                          {formatDate(row.dateUpdated)}
                        </td>
                        {!isWixAuth && !isCreAuth && (
                          <>
                            <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                              {row.isActive ? "Yes" : "No"}
                            </td>

                            <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                              {row.organizationId !== undefined
                                ? row.organizationId
                                : "N/A"}
                            </td>
                            <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                              {row.sectionId || "N/A"}
                            </td>
                            <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                              {row.sectionTypeId || "N/A"}
                            </td>
                          </>
                        )}
                      </>
                    ) : displayFooterItems ? (
                      <>
                        <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                          Templates Footer
                        </td>
                        <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                          {formatDate(row.dateCreated)}
                        </td>
                        <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                          {formatDate(row.dateUpdated)}
                        </td>
                        {!isWixAuth && !isCreAuth && (
                          <>
                            <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                              {row.isActive ? "Yes" : "No"}
                            </td>
                            <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                              {row.organizationId !== undefined
                                ? row.organizationId
                                : "N/A"}
                            </td>
                            <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                              {row.sectionId || "N/A"}
                            </td>
                            <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                              {row.sectionTypeId || "N/A"}
                            </td>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {!hideTemplateFields && (
                          <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                            {row.template || "N/A"}
                          </td>
                        )}
                        <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                          {row.isDefault ? "Yes" : "No"}
                        </td>
                        <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                          {row.language || "N/A"}
                        </td>
                        <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                          {isWixAuth || isCreAuth
                            ? row.userName ||
                              (isWixAuth ? wixUser?.email : creUser?.email) ||
                              "N/A"
                            : row.userId?.toString() || "N/A"}
                        </td>
                        <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                          <div className="flex gap-4 justify-center text-sm">
                            {(() => {
                              const isDefaultTemplate = !!row.isDefault;
                              // Radical only: must be admin to edit; Wix uses existing default-template rule
                              const canEditOrDeactivate =
                                canCreateOrEditTemplates &&
                                (!isDefaultTemplate || canManageDefaultTemplates);
                              return (
                                <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!canEditOrDeactivate) return;
                                handleEditClick(row);
                              }}
                              disabled={!canEditOrDeactivate}
                              title={
                                canEditOrDeactivate
                                  ? "Edit template"
                                  : !canCreateOrEditTemplates
                                  ? "Admin only"
                                  : "Only admin can edit default templates"
                              }
                              className={`${
                                canEditOrDeactivate
                                  ? "text-blue-500 hover:text-blue-700 hover:underline"
                                  : "text-gray-400"
                              }`}
                            >
                              Edit
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!canEditOrDeactivate) return;
                                setItemToDelete(row);
                                setShowDeleteModal(true);
                              }}
                              disabled={!canEditOrDeactivate}
                              title={
                                canEditOrDeactivate
                                  ? "Deactivate template"
                                  : !canCreateOrEditTemplates
                                  ? "Admin only"
                                  : "Only admin can deactivate default templates"
                              }
                              className={`${
                                canEditOrDeactivate
                                  ? "text-red-500 hover:text-red-700 hover:underline"
                                  : "text-gray-400"
                              }`}
                            >
                              Deactivate
                            </button>
                                </>
                              );
                            })()}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
          </tbody>
        </table>
      )}
      {/* Macros Table */}
      {selectedCard === 0 && (
        <div className="">
          {showMacroSearch && (
            <div className="mb-4">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={`Search by ${currentMacroFilterLabel}`}
                className="px-4 py-2 border rounded w-full bg-gray-200 dark:bg-gray-700 dark:text-gray-200"
              />
            </div>
          )}
          <table className="table-auto border bg-white dark:bg-gray-800 min-w-full text-left">
            <thead className="text-sm">
              <tr>
                {macroHeaders.map((header) => (
                  <th
                    key={header.key}
                    className="border px-2 py-2 min-w-[150px] cursor-pointer dark:text-gray-200"
                    onClick={() => handleMacroFilterTypeChange(header.key)}
                  >
                    <div className="flex gap-2 items-center">
                      <p>{header.label}</p>
                      {header.label !== "Edits" && (
                        <span className="bg-gray-200 dark:bg-gray-700 w-6 h-6 rounded-full flex justify-center items-center">
                          <RiFilter2Line className="text-gray-900 dark:text-gray-200" />
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-xs">
              {loading
                ? [...Array(rowsPerPage)].map((_, index) => (
                    <tr key={index}>
                      {[...Array(macroHeaders.length)].map((_, i) => (
                        <td
                          key={i}
                          className="border border-gray-300 dark:border-gray-700 px-4 py-2"
                        >
                          <Skeleton height={20} />
                        </td>
                      ))}
                    </tr>
                  ))
                : currentMacrosData.map((macro, index) => (
                    <tr
                      key={index}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {!isWixAuth && !isCreAuth && (
                        <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                          {macro.macroId || "N/A"}
                        </td>
                      )}
                      <td className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans">
                        {macro.macroName || "N/A"}
                      </td>
                      <td
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditMacro(macro);
                        }}
                        className="border border-gray-300 dark:border-gray-700 px-4 py-2 font-dmSans"
                      >
                        <button>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Pagination controls */}
      <div className="flex justify-between items-center mt-4">
        <button
          className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-gray-200"
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-gray-200"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next page
        </button>
      </div>
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-200">
              Confirm Deactivation
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to deactivate this{" "}
              {selectedCard === 0 ? "macro" : "template"} for all users?
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default TableSection;
